import { AUTHORS } from './authors';
import { PageMeta } from 'constants/pages';
import { BOOK_A_DEMO_LINK } from 'constants/common';
import { EHashtag, TCardProps } from 'types/common';
import { ArticleSections } from 'types/articles';

import HeaderImage from 'assets/resources/thermal-camera-sensors-ailivesim-platform/HeaderImage.webp';
import ThermalCapabilitiesImage from 'assets/resources/thermal-camera-sensors-ailivesim-platform/ThermalCapabilitiesImage.webp';
import MultiSensorPlatformImage from 'assets/resources/thermal-camera-sensors-ailivesim-platform/MultiSensorPlatformImage.webp';
import EfficiencyCostImpactImage from 'assets/resources/thermal-camera-sensors-ailivesim-platform/EfficiencyCostImpactImage.webp';

const commonData = {
  ...AUTHORS.AILiveSim,
  title: "How Thermal Camera Sensors Works in AILiveSim's Platform",
  publishDate: 'January 2, 2026',
  minutesOfRead: 10,
};

export const secondJanuary2026ArticleMeta: PageMeta = {
  url: '/articles/thermal-camera-sensors-ailivesim-platform',
  title: commonData.title,
  description:
    "AILiveSim's thermal camera simulation generates physics-based infrared frames synchronized across multi-sensor stacks, with ground truth for fusion training and systematic edge-case discovery before expensive field trials.",
};

export const secondJanuary2026ArticleData: ArticleSections[] = [
  {
    resourceType: 'Article',
    image: HeaderImage,
    imageCaption: 'Long-Wave Infrared view from a forest',
    type: 'header',
    linkedInPostId: 'https://www.linkedin.com/company/ailivesim/',
    text: (
      <>
        <p>
          Real autonomous systems don&apos;t rely on single sensors, they fuse data from cameras,
          LiDAR, radar, thermal imaging, GNSS, and IMU to perceive their environment completely.
          Testing these sensors in isolation isn&apos;t enough. Multi-sensor simulation enables
          teams to validate perception under realistic conditions, test edge cases like low
          visibility and weather degradation, and generate synchronized data with ground truth at
          scale. AILiveSim&apos;s platform now includes thermal camera simulation, completing a
          comprehensive sensor suite that enables defense and maritime customers to de-risk
          deployment before committing to costly field trials.
        </p>
      </>
    ),
    ...commonData,
  },

  {
    type: 'common',
    title: 'Understanding Thermal Camera Technology',
    text: (
      <>
        <p>
          Thermal cameras detect infrared radiation emitted by objects, converting heat signatures
          into visible images. Unlike optical cameras dependent on reflected light, thermal sensors
          operate independently of ambient illumination, making them essential for low-light and
          nighttime operations. The technology spans long-wave infrared (LWIR) and short-wave
          infrared (SWIR), each using specialized materials with infrared-detecting capabilities
          optimized for different detection requirements, ranges, and atmospheric conditions.
        </p>
        <p>
          In defense applications, thermal cameras detect personnel, vehicles, and equipment
          through darkness, camouflage, and obscure threats invisible to standard optical sensors.
          Naval operations require thermal detection of anomalies and objects critical for mission
          execution. Maritime operators use thermal imaging to identify vessels, swimmers, kayakers,
          wildlife, and floating obstacles when visual detection fails, particularly during night
          operations and challenging sea states. For autonomous systems in these domains, thermal
          perception isn&apos;t optional, it&apos;s essential for reliable 24/7 operational
          capability.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: "AILiveSim's Thermal Capabilities",
    image: ThermalCapabilitiesImage,
    text: (
      <>
        <p>
          Our platform generates physics-based thermal imagery synchronized with the full sensor
          suite: cameras, LiDAR, radar, GNSS, and IMU. The simulation models heat emission
          characteristics, atmospheric effects, and material properties to produce realistic
          infrared data with built-in ground truth for precise performance evaluation. This
          simulation-in-the-loop approach integrates directly into AI development, training, and
          testing cycles, enabling closed-loop validation where algorithms process fused multi-sensor
          inputs exactly as they would in deployment.
        </p>
        <p>
          Thermal simulation opens critical validation possibilities. Teams test detection in
          complete darkness, validate sensor fusion combining thermal and visual streams, and
          generate scenarios with varying heat signatures across weather conditions and times of
          day. Defense customers validate detection of anomalies and targets that optical sensors
          miss entirely. Our Intelligent System Testing framework enables systematic searches for
          edge cases and failure modes, acting as an automated red team that finds where algorithms
          break before deployment reveals them.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Multi-Sensor Platform Benefits',
    image: MultiSensorPlatformImage,
    text: (
      <>
        <p>
          Thermal simulation strengthens AILiveSim&apos;s value as a comprehensive validation
          platform. Instead of testing sensors in isolation, teams validate complete perception
          pipelines under realistic conditions. Edge cases involving low visibility, weather, and
          sensor degradation become standard test coverage rather than untested risks that surface
          only in deployment. The platform generates synchronized data across all modalities at
          scale, supporting both synthetic data generation for robust AI training and closed-loop
          autonomy validation.
        </p>
        <p>
          Combined visual and thermal simulation provides richer operational insight than either
          modality alone. Defense and maritime customers de-risk deployment through comprehensive
          virtual testing, achieving the defensible coverage that commanders and certifiers require
          for autonomous system approval.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Efficiency and Cost Impact',
    image: EfficiencyCostImpactImage,
    text: (
      <>
        <p>
          Multi-sensor simulation on one platform saves significant time and resources by
          eliminating separate testing infrastructure for each sensor type. Teams simulate diverse
          scenarios combining visual and thermal conditions, collecting comprehensive data that
          would require multiple expensive field campaigns to gather physically. Customers achieve
          development cycle reductions up to 60%, with training compressed from weeks to hours
          through parallel cloud simulation running thousands of instances. Physical data
          collection is drastically reduced, freeing engineering resources for actual development
          work rather than logistics.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Explore Further Today',
    text: (
      <>
        <p>
          Ready to explore how thermal simulation can enhance your autonomous system development?{' '}
          <strong>
            <a href={BOOK_A_DEMO_LINK} target="_blank" rel="noopener noreferrer">
              Schedule a demo session with us to learn more.
            </a>
          </strong>
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Resources',
    text: (
      <>
        <ul>
          <li>
            Read our White Paper about the sim to real gap:{' '}
            <a
              href="https://ailivesim.com/whitepapers/using-synthetic-data-from-ailivesim"
              target="_blank"
              rel="noopener noreferrer"
            >
              Using Synthetic Data from AILiveSim to Train Robust Maritime Detection Models
            </a>
          </li>
        </ul>
      </>
    ),
  },
];

export const secondJanuary2026ArticleCard: TCardProps = {
  description:
    "This article describes AILiveSim's thermal sim for night and low-visibility autonomy. It makes IR frames synced across sensors, with ground truth for fusion training. See how it finds edge cases early and shrinks field trials.",
  image: HeaderImage,
  type: 'Article',
  category: 'articles',
  postUrl: secondJanuary2026ArticleMeta.url,
  ...commonData,
  hashTags: [
    EHashtag.SyntheticData,
    EHashtag.ComputerVision,
    EHashtag.AISimulation,
    EHashtag.SafetyValidation,
  ],
};
